import { useState, useEffect, useRef } from "react";
import { ReactComponent as SchrankASVG } from "./assets/schrankA.svg";
import { ReactComponent as SchrankBSVG } from "./assets/schrankB.svg";
import { ReactComponent as WerkbankSVG } from "./assets/werkbank.svg";

const schraenke = [
  { name: "Schrank A", width: 80, height: 60, color: "#3b82f6", SVG: SchrankASVG, price: 199 },
  { name: "Schrank B", width: 100, height: 60, color: "#6366f1", SVG: SchrankBSVG, price: 249 },
  { name: "Werkbank", width: 120, height: 70, color: "#f59e0b", SVG: WerkbankSVG, price: 299 },
];

export default function WerkstattKonfigurator() {
  const [length, setLength] = useState(600);
  const [width, setWidth] = useState(400);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedItems, setSelectedItems] = useState(() => {
    const saved = localStorage.getItem("werkstatt-items");
    return saved ? JSON.parse(saved) : [];
  });
  const [dragItem, setDragItem] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const gridSize = 20;
  const canvasRef = useRef(null);

  useEffect(() => {
    drawWorkshop();
  }, [length, width, selectedItems, zoom, pan]);

  useEffect(() => {
    localStorage.setItem("werkstatt-items", JSON.stringify(selectedItems));
  }, [selectedItems]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left - pan.x) / zoom;
      const mouseY = (e.clientY - rect.top - pan.y) / zoom;
      for (const item of selectedItems) {
        const w = item.rotation % 180 === 0 ? item.width : item.height;
        const h = item.rotation % 180 === 0 ? item.height : item.width;
        if (
          mouseX > item.x &&
          mouseX < item.x + w &&
          mouseY > item.y &&
          mouseY < item.y + h
        ) {
          setDragItem(item);
          setOffset({ x: mouseX - item.x, y: mouseY - item.y });
          return;
        }
      }
      if (zoom > 1) {
        setIsPanning(true);
        setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      }
    };
    const handleMouseUp = () => {
      setDragItem(null);
      setIsPanning(false);
    };
    const handleMouseMove = (e) => {
      if (isPanning) {
        const newPanX = e.clientX - panStart.x;
        const newPanY = e.clientY - panStart.y;

        const maxPanX = (zoom - 1) * length;
        const maxPanY = (zoom - 1) * width;

        setPan({
          x: Math.max(Math.min(newPanX, maxPanX), 0),
          y: Math.max(Math.min(newPanY, maxPanY), 0),
        });
        return;
      }
      if (!dragItem) return;
      const rect = canvas.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left - pan.x) / zoom;
      const mouseY = (e.clientY - rect.top - pan.y) / zoom;

      const snapX = Math.round((mouseX - offset.x) / gridSize) * gridSize;
      const snapY = Math.round((mouseY - offset.y) / gridSize) * gridSize;

      const updatedItem = { ...dragItem, x: snapX, y: snapY };

      const w = updatedItem.rotation % 180 === 0 ? updatedItem.width : updatedItem.height;
      const h = updatedItem.rotation % 180 === 0 ? updatedItem.height : updatedItem.width;

      const withinBounds =
        updatedItem.x >= 0 &&
        updatedItem.y >= 0 &&
        updatedItem.x + w <= length &&
        updatedItem.y + h <= width;

      if (
        withinBounds &&
        !hasCollision(updatedItem, selectedItems.filter((i) => i.id !== updatedItem.id))
      ) {
        setSelectedItems((prev) =>
          prev.map((i) => (i.id === updatedItem.id ? updatedItem : i))
        );
      }
    };
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mousemove", handleMouseMove);
    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, [dragItem, offset, selectedItems, length, width, zoom, pan, isPanning, panStart]);

  const handleInputChange = (e, setter) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) setter(value);
  };

  const toggleItem = (item) => {
    const uniqueId = Date.now() + Math.random();
    const newItem = {
      ...item,
      id: uniqueId,
      x: 0,
      y: 0,
      width: item.width,
      height: item.height,
      rotation: 0,
    };
    const position = findFreeSpot(newItem);
    if (position) {
      newItem.x = position.x;
      newItem.y = position.y;
      setSelectedItems((prev) => [...prev, newItem]);
    } else {
      alert("Kein Platz mehr für diesen Schrank.");
    }
  };

  const findFreeSpot = (newItem) => {
    for (let y = 0; y <= width - newItem.height; y += gridSize) {
      for (let x = 0; x <= length - newItem.width; x += gridSize) {
        const testItem = { ...newItem, x, y };
        if (!hasCollision(testItem, selectedItems)) {
          return { x, y };
        }
      }
    }
    return null;
  };

  const deleteItem = (id) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const rotateItem = (id) => {
    setSelectedItems((prev) => {
      return prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          rotation: (item.rotation + 90) % 360,
        };
      });
    });
  };

  const hasCollision = (item, others) => {
    const w = item.rotation % 180 === 0 ? item.width : item.height;
    const h = item.rotation % 180 === 0 ? item.height : item.width;
    return others.some((other) => {
      const ow = other.rotation % 180 === 0 ? other.width : other.height;
      const oh = other.rotation % 180 === 0 ? other.height : other.width;
      return (
        item.x < other.x + ow &&
        item.x + w > other.x &&
        item.y < other.y + oh &&
        item.y + h > other.y
      );
    });
  };

  const drawWorkshop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    ctx.beginPath();
    ctx.rect(0, 0, length, width);
    ctx.clip();

    ctx.fillStyle = "#4ade80";
    ctx.fillRect(0, 0, length, width);

    ctx.strokeStyle = "#000";
    ctx.strokeRect(0, 0, length, width);

    ctx.fillStyle = "#000";
    ctx.font = "16px sans-serif";
    ctx.fillText(`${length}cm x ${width}cm`, 10, 20);

    for (const item of selectedItems) {
      const w = item.rotation % 180 === 0 ? item.width : item.height;
      const h = item.rotation % 180 === 0 ? item.height : item.width;
      ctx.save();
      ctx.translate(item.x + w / 2, item.y + h / 2);
      ctx.rotate((item.rotation * Math.PI) / 180);
      ctx.fillStyle = item.color;
      ctx.fillRect(-w / 2, -h / 2, w, h);

      ctx.strokeStyle = "#000";
      ctx.strokeRect(-w / 2, -h / 2, w, h);

      ctx.fillStyle = "#fff";
      ctx.font = "12px sans-serif";
      ctx.fillText(item.name, -w / 2 + 5, -h / 2 + 15);
      ctx.restore();
    }

    ctx.restore();
  };

  const totalPrice = selectedItems.reduce((sum, item) => sum + item.price, 0);

  return (
    <div style={{ display: "flex", padding: "1rem" }}>
      <div style={{ width: "200px", marginRight: "1rem" }}>
        <h2 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "0.5rem" }}>Werkstattschränke</h2>
        {schraenke.map((item, index) => (
          <div key={index} style={{ marginBottom: "1rem", cursor: "pointer" }} onClick={() => toggleItem(item)}>
            <item.SVG width={100} height={60} />
            <div style={{ textAlign: "center" }}>{item.name} ({item.price}€)</div>
          </div>
        ))}
        <div style={{ marginTop: "1rem" }}>
          <button onClick={() => setZoom((z) => Math.min(z + 0.25, 2))}>Zoom In</button>
          <button onClick={() => setZoom((z) => Math.max(z - 0.25, 1))} style={{ marginLeft: "0.5rem" }}>Zoom Out</button>
        </div>
      </div>

      <div style={{ flexGrow: 1 }}>
        <h1 style={{ fontSize: "1.25rem", fontWeight: "bold" }}>
          Werkstatt-Konfigurator (Premium Line)
        </h1>

        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
              Länge (cm)
            </label>
            <input
              type="number"
              value={length}
              onChange={(e) => handleInputChange(e, setLength)}
              style={{ border: "1px solid #ccc", padding: "0.5rem", borderRadius: "0.25rem", width: "8rem" }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
              Breite (cm)
            </label>
            <input
              type="number"
              value={width}
              onChange={(e) => handleInputChange(e, setWidth)}
              style={{ border: "1px solid #ccc", padding: "0.5rem", borderRadius: "0.25rem", width: "8rem" }}
            />
          </div>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", marginBottom: "0.5rem" }}>
            Werkstattfläche (Vorschau)
          </h2>
          <div style={{ width: `${length}px`, height: `${width}px`, overflow: "hidden", border: "1px solid #ccc" }}>
            <canvas
              ref={canvasRef}
              style={{ width: "100%", height: "100%", background: "#eee", cursor: isPanning ? "grabbing" : "move" }}
            />
          </div>
        </div>
      </div>

      <div style={{ width: "250px", marginLeft: "1rem" }}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", marginBottom: "0.5rem" }}>
          Übersicht & Preise
        </h2>
        <ul>
          {selectedItems.map((item) => (
            <li key={item.id}>
              {item.name} – {item.price}€
              <button
                onClick={() => deleteItem(item.id)}
                style={{ marginLeft: "1rem", color: "red", background: "none", border: "none", cursor: "pointer" }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
        <p><strong>Gesamtpreis: {totalPrice}€</strong></p>
        <p><strong>Gesamtfläche: {(length * width / 10000).toFixed(2)} m²</strong></p>
        <button style={{ marginTop: "1rem", padding: "0.5rem 1rem", background: "#10b981", color: "white", border: "none", borderRadius: "0.25rem", cursor: "pointer" }}>In den Warenkorb</button>
      </div>
    </div>
  );
}
